import { BaseService } from './BaseService';
import type { PagedResult } from '../types/tenant';
import type {
  ToolDto,
  ToolDetailDto,
  ToolExecutionLogDto,
  ToolFilters,
  CreateToolRequest,
  UpdateToolRequest,
  ToolExecutionResult,
  ToolImpactPreview,
} from '../types/tool';

/**
 * Tool service for managing and executing tools.
 */
export class ToolService extends BaseService {
  /**
   * Gets a paginated list of tools with optional filtering.
   */
  async getTools(filters?: ToolFilters): Promise<PagedResult<ToolDto>> {
    const queryParams = new URLSearchParams();
    if (filters?.pageNumber) queryParams.append('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) queryParams.append('pageSize', filters.pageSize.toString());
    if (filters?.category) queryParams.append('category', filters.category);
    if (filters?.isDefault !== undefined) queryParams.append('isDefault', filters.isDefault.toString());
    if (filters?.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());
    if (filters?.searchTerm) queryParams.append('searchTerm', filters.searchTerm);

    const url = `/tools${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedResult<ToolDto>>(url);
  }

  /**
   * Gets default tools.
   */
  async getDefaultTools(): Promise<ToolDto[]> {
    return this.get<ToolDto[]>('/tools/default');
  }

  /**
   * Gets a tool by ID.
   */
  async getTool(id: string): Promise<ToolDetailDto> {
    return this.get<ToolDetailDto>(`/tools/${id}`);
  }

  /**
   * Gets a tool by name.
   */
  async getToolByName(name: string): Promise<ToolDetailDto> {
    return this.get<ToolDetailDto>(`/tools/name/${encodeURIComponent(name)}`);
  }

  /**
   * Creates a tool.
   */
  async createTool(data: CreateToolRequest): Promise<ToolDetailDto> {
    return this.post<ToolDetailDto>('/tools', {
      ...data,
      riskLevel: data.riskLevel ?? 'Low',
      isDefault: data.isDefault ?? false,
      isActive: data.isActive ?? true,
      isSystem: data.isSystem ?? false,
      version: data.version ?? '1.0.0',
      requiresApproval: data.requiresApproval ?? false,
      inputSchemaJson: data.inputSchemaJson ?? '{}',
      outputSchemaJson: data.outputSchemaJson ?? '{}',
    });
  }

  /**
   * Updates a tool.
   */
  async updateTool(id: string, data: UpdateToolRequest): Promise<ToolDetailDto> {
    return this.put<ToolDetailDto>(`/tools/${id}`, data);
  }

  /**
   * Deletes a tool.
   */
  async deleteTool(id: string): Promise<void> {
    return this.delete<void>(`/tools/${id}`);
  }

  /**
   * Executes a tool by name.
   * Note: The backend executes by tool ID; this helper resolves by name first.
   */
  async executeTool(
    toolName: string,
    parameters: Record<string, any>,
    isDryRun: boolean,
    reason?: string
  ): Promise<ToolExecutionResult> {
    const tool = await this.getToolByName(toolName);
    return this.post<ToolExecutionResult>(`/tools/${tool.id}/execute`, {
      toolName,
      parameters,
      isDryRun,
      reason,
    });
  }

  /**
   * Previews the impact of a tool by name.
   * Note: The backend previews by tool ID; this helper resolves by name first.
   */
  async previewTool(toolName: string, parameters: Record<string, any>): Promise<ToolImpactPreview> {
    const tool = await this.getToolByName(toolName);
    return this.post<ToolImpactPreview>(`/tools/${tool.id}/preview`, {
      toolName,
      parameters,
      isDryRun: true,
    });
  }

  /**
   * Gets tool execution history.
   */
  async getToolExecutions(toolId: string, pageNumber = 1, pageSize = 25): Promise<PagedResult<ToolExecutionLogDto>> {
    const queryParams = new URLSearchParams();
    queryParams.append('pageNumber', pageNumber.toString());
    queryParams.append('pageSize', pageSize.toString());

    return this.get<PagedResult<ToolExecutionLogDto>>(`/tools/${toolId}/executions?${queryParams.toString()}`);
  }
}

export const toolService = new ToolService();

